import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { PromptBlock } from "@/components/PromptBlock";
import API from "@/lib/api";
import { Copy, Check, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STAGE_TABS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "tested", label: "Tested" },
  { value: "production", label: "Production" },
  { value: "deprecated", label: "Deprecated" },
];

export function PromptResults({ promptData, activeMode, activeFilters, onBlockUpdate, onModeBlocksReplaced, standardBlocks, generatingModes }) {
  const [bundleCopied, setBundleCopied] = useState(false);
  const [sharpening, setSharpening] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [prevFilters, setPrevFilters] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [stageFilter, setStageFilter] = useState("all");

  // Auto-adapt blocks when filters change (debounced)
  const adaptTimerRef = useRef(null);
  const filtersKey = activeFilters.sort().join(",");

  // Reset selection when mode changes
  useEffect(() => { setSelectedIndices(new Set()); }, [activeMode]);

  useEffect(() => {
    if (!promptData) return;
    if (adaptTimerRef.current) clearTimeout(adaptTimerRef.current);

    if (activeFilters.length === 0) {
      const hasBaseBlocks = !!(promptData.base_blocks && promptData.base_blocks[activeMode]);
      if (hasBaseBlocks && promptData.id) {
        API.post(`/prompts/${promptData.id}/reset-blocks`, { mode: activeMode })
          .then(({ data }) => {
            if (onModeBlocksReplaced) onModeBlocksReplaced(activeMode, data.blocks);
          })
          .catch(() => {});
      }
      setPrevFilters([]);
      return;
    }

    if (filtersKey === prevFilters.sort().join(",")) return;

    adaptTimerRef.current = setTimeout(async () => {
      if (!promptData.id) return;
      setAdapting(true);
      try {
        const { data } = await API.post(`/prompts/${promptData.id}/adapt-blocks`, {
          mode: activeMode,
          filters: activeFilters,
          concept: promptData.concept || "",
        });
        if (onModeBlocksReplaced) onModeBlocksReplaced(activeMode, data.blocks);
        setPrevFilters([...activeFilters]);
      } catch (e) {
        console.error("Auto-adapt error:", e);
      } finally {
        setAdapting(false);
      }
    }, 800);

    return () => { if (adaptTimerRef.current) clearTimeout(adaptTimerRef.current); };
  }, [filtersKey, activeMode, promptData?.id, promptData?.base_blocks, onModeBlocksReplaced]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!promptData) return null;

  const blocks = promptData.blocks || {};

  // Resolve blocks for the active mode — never fall back to raw templates
  let modeBlocks;
  const promptBlocks = blocks[activeMode] || [];
  modeBlocks = promptBlocks;

  // Filter by tag filters
  const tagFiltered = activeFilters.length === 0
    ? modeBlocks
    : modeBlocks.filter((b) => {
        const blockTags = b.tags || [];
        return activeFilters.some((f) => blockTags.includes(f));
      });

  // Filter by stage
  const filteredBlocks = stageFilter === "all"
    ? tagFiltered
    : tagFiltered.filter((b) => (b.stage || "draft") === stageFilter);

  // Stage counts for tabs
  const stageCounts = {};
  tagFiltered.forEach((b) => {
    const s = b.stage || "draft";
    stageCounts[s] = (stageCounts[s] || 0) + 1;
  });
  stageCounts["all"] = tagFiltered.length;

  // Group blocks by their first matching filter tag
  const grouped = {};
  if (activeFilters.length === 0) {
    grouped["All Blocks"] = filteredBlocks;
  } else {
    filteredBlocks.forEach((block) => {
      const matchingTag = activeFilters.find((f) => (block.tags || []).includes(f)) || "Other";
      if (!grouped[matchingTag]) grouped[matchingTag] = [];
      grouped[matchingTag].push(block);
    });
  }

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
  };

  const handleCopySelected = async () => {
    const selected = filteredBlocks.filter((_, i) => selectedIndices.has(modeBlocks.indexOf(filteredBlocks[i])));
    if (selected.length === 0) return;
    const text = selected.map((b) => `--- ${b.title} ---\n${b.content}`).join("\n\n");
    await copyToClipboard(text);
    toast.success(`Copied ${selected.length} blocks`);
    setSelectedIndices(new Set());
  };

  const handleCopyAll = async () => {
    const text = filteredBlocks.map((b) => `--- ${b.title} ---\n${b.content}`).join("\n\n");
    await copyToClipboard(text);
    setBundleCopied(true);
    setTimeout(() => setBundleCopied(false), 2000);
  };

  const handleSelectAll = () => {
    if (selectedIndices.size === filteredBlocks.length) {
      setSelectedIndices(new Set());
    } else {
      const all = new Set(filteredBlocks.map((b) => modeBlocks.indexOf(b)));
      setSelectedIndices(all);
    }
  };

  const toggleSelect = (originalIndex) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(originalIndex)) { next.delete(originalIndex); } else { next.add(originalIndex); }
      return next;
    });
  };

  const handleSharpenBlocks = async () => {
    if (!promptData.id || activeFilters.length === 0) return;
    setSharpening(true);
    try {
      const { data } = await API.post(`/prompts/${promptData.id}/refine-blocks`, { mode: activeMode, filters: activeFilters });
      if (onModeBlocksReplaced) onModeBlocksReplaced(activeMode, data.blocks);
      toast.success("Blocks sharpened", { description: `${data.blocks.length} blocks refined` });
    } catch (e) {
      toast.error("Sharpening failed", { description: e.response?.data?.detail || "Try again." });
    } finally { setSharpening(false); }
  };

  const handleResetToBase = async () => {
    if (!promptData.id) return;
    setResetting(true);
    try {
      const { data } = await API.post(`/prompts/${promptData.id}/reset-blocks`, { mode: activeMode });
      if (onModeBlocksReplaced) onModeBlocksReplaced(activeMode, data.blocks);
      toast.success("Reset to base", { description: `${data.blocks.length} blocks restored.` });
    } catch (e) {
      toast.error("Reset failed", { description: e.response?.data?.detail || "No base blocks." });
    } finally { setResetting(false); }
  };

  const handleBlockStageChange = (originalIndex, newStage) => {
    if (!onBlockUpdate) return;
    const block = modeBlocks[originalIndex];
    if (block) {
      onBlockUpdate(activeMode, originalIndex, block.content, { ...block, stage: newStage });
    }
  };

  const handleBlockDuplicated = (newBlock) => {
    // Append new block to mode blocks
    if (!promptData.id) return;
    const updatedBlocks = [...modeBlocks, newBlock];
    if (onModeBlocksReplaced) onModeBlocksReplaced(activeMode, updatedBlocks);
  };

  const hasBaseBlocks = !!(promptData.base_blocks && promptData.base_blocks[activeMode]);
  const modeLabel = activeMode.charAt(0).toUpperCase() + activeMode.slice(1);
  const hasSelection = selectedIndices.size > 0;

  return (
    <div data-testid="prompt-results">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h2 className="font-heading text-xl font-bold text-[var(--pf-text)]">{modeLabel}</h2>
          <span className="text-xs text-[var(--pf-text-muted)]">
            {filteredBlocks.length} of {modeBlocks.length} blocks
          </span>
        </div>
        <div className="flex items-center gap-2">
          {adapting && (
            <span className="flex items-center gap-1.5 text-xs text-[var(--pf-accent)] font-medium" data-testid="adapting-indicator">
              <Loader2 className="w-3 h-3 animate-spin" />Adapting...
            </span>
          )}
          {activeFilters.length > 0 && !adapting && (
            <Button size="sm" onClick={handleSharpenBlocks} disabled={sharpening} data-testid="sharpen-blocks-button"
              className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-8 text-xs font-medium">
              {sharpening ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
              {sharpening ? "Sharpening..." : "Re-sharpen"}
            </Button>
          )}
          {hasBaseBlocks && (
            <Button variant="outline" size="sm" onClick={handleResetToBase} disabled={resetting} data-testid="reset-base-button"
              className="rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
              {resetting ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1.5" />}
              Reset
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopyAll} data-testid="copy-all-button"
            className="rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
            {bundleCopied ? <Check className="w-3 h-3 mr-1.5 text-[#16A34A]" /> : <Copy className="w-3 h-3 mr-1.5" />}
            {bundleCopied ? "Copied" : "Copy All"}
          </Button>
        </div>
      </div>

      {/* Stage tabs + batch actions */}
      <div className="flex items-center justify-between mb-4 border-b border-[var(--pf-border)] pb-2">
        <div className="flex items-center gap-1" data-testid="stage-tabs">
          {STAGE_TABS.map(({ value, label }) => {
            const count = stageCounts[value] || 0;
            const isActive = stageFilter === value;
            return (
              <button
                key={value}
                onClick={() => { setStageFilter(value); setSelectedIndices(new Set()); }}
                data-testid={`stage-tab-${value}`}
                className={`text-xs px-2.5 py-1.5 font-medium transition-colors duration-100 ${
                  isActive
                    ? "text-[var(--pf-accent)] border-b-2 border-[var(--pf-accent)]"
                    : "text-[var(--pf-text-muted)] hover:text-[var(--pf-text-secondary)]"
                }`}
              >
                {label} {count > 0 && <span className="text-[10px] ml-0.5 opacity-60">{count}</span>}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          {filteredBlocks.length > 0 && (
            <button onClick={handleSelectAll} data-testid="select-all-blocks" className="text-[11px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium">
              {selectedIndices.size === filteredBlocks.length ? "Deselect all" : "Select all"}
            </button>
          )}
          {hasSelection && (
            <Button size="sm" onClick={handleCopySelected} data-testid="copy-selected-button"
              className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-7 text-[11px] font-medium px-2.5">
              <Copy className="w-3 h-3 mr-1" />Copy {selectedIndices.size} selected
            </Button>
          )}
        </div>
      </div>

      {/* Blocks */}
      {filteredBlocks.length > 0 ? (
        <div className={`space-y-6 ${adapting ? "opacity-50 pointer-events-none" : ""}`} style={{ transition: "opacity 200ms" }}>
          {Object.entries(grouped).map(([groupName, groupBlocks]) => (
            <div key={groupName}>
              {activeFilters.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--pf-accent)]">{groupName}</span>
                  <span className="text-[10px] text-[var(--pf-text-muted)]">{groupBlocks.length}</span>
                  <div className="flex-1 h-px bg-[var(--pf-border)]" />
                </div>
              )}
              <div className="grid grid-cols-1 gap-3">
                {groupBlocks.map((block) => {
                  const originalIndex = modeBlocks.indexOf(block);
                  return (
                    <PromptBlock
                      key={`${activeMode}-${originalIndex}-${block.stage || "draft"}`}
                      promptId={promptData.id}
                      category={activeMode}
                      index={originalIndex}
                      title={block.title}
                      content={block.content}
                      tags={block.tags || []}
                      stage={block.stage}
                      quality={block.quality}
                      selected={selectedIndices.has(originalIndex)}
                      onSelect={toggleSelect}
                      onUpdate={(newContent) => onBlockUpdate && onBlockUpdate(activeMode, originalIndex, newContent)}
                      onRegenerated={(newBlock) => onBlockUpdate && onBlockUpdate(activeMode, originalIndex, newBlock.content, newBlock)}
                      onStageChange={(newStage) => handleBlockStageChange(originalIndex, newStage)}
                      onDuplicated={handleBlockDuplicated}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-[var(--pf-border)] p-12 text-center" data-testid="mode-empty-state">
          {generatingModes && generatingModes[activeMode] === "loading" ? (
            <div>
              <Loader2 className="w-6 h-6 animate-spin text-[var(--pf-accent)] mx-auto mb-3" />
              <p className="text-sm text-[var(--pf-text-secondary)]">Generating {modeLabel} blocks...</p>
            </div>
          ) : (
            <p className="text-sm text-[var(--pf-text-muted)]">
              {stageFilter !== "all"
                ? `No ${stageFilter} blocks in this mode. Try a different stage filter.`
                : activeFilters.length > 0
                  ? "No blocks match the selected filters."
                  : "No blocks in this mode yet. Pull from the library or generate new ones."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
